import React from "react";

/** Mirrors backend/utils/signal_thresholds.py::SignalThresholds field-for-
 * field — these are the exact keys round-tripped through the
 * signal_engine_thresholds vendor_config value (see
 * backend/utils/signal_engine_thresholds_config.py). */
export interface SignalEngineThresholds {
  BH_STRONG: number;
  BH_STABLE: number;
  BH_CONCERN: number;
  RISK_HIGH: number;
  RISK_MEDIUM: number;
  KPI_WARN: number;
  AIRCOVER: number;
  FIT_TIER_A: number;
  FIT_TIER_B: number;
  FIT_TIER_C: number;
  FIT_TIER_D: number;
  OPP_UPGRADE: number;
  OPP_MIN_EXPANSION: number;
  OPP_FALLBACK_STRONG: number;
  OPP_FALLBACK_POSSIBLE: number;
  PORTFOLIO_RISK_HIGH: number;
  PORTFOLIO_RISK_LOW: number;
  EWMA_ALPHA: number;
  EWMA_COLD_START_DAYS: number;
  UPGRADE_CONSISTENT_READINGS: number;
  DOWNGRADE_CONSISTENT_READINGS: number;
}

export const DEFAULT_SIGNAL_ENGINE_THRESHOLDS: SignalEngineThresholds = {
  BH_STRONG: 70,
  BH_STABLE: 50,
  BH_CONCERN: 1,
  RISK_HIGH: 60,
  RISK_MEDIUM: 35,
  KPI_WARN: 50,
  AIRCOVER: 65,
  FIT_TIER_A: 85,
  FIT_TIER_B: 70,
  FIT_TIER_C: 55,
  FIT_TIER_D: 40,
  OPP_UPGRADE: 70,
  OPP_MIN_EXPANSION: 55,
  OPP_FALLBACK_STRONG: 85,
  OPP_FALLBACK_POSSIBLE: 75,
  PORTFOLIO_RISK_HIGH: 50,
  PORTFOLIO_RISK_LOW: 30,
  EWMA_ALPHA: 0.25,
  EWMA_COLD_START_DAYS: 90,
  UPGRADE_CONSISTENT_READINGS: 2,
  DOWNGRADE_CONSISTENT_READINGS: 1,
};

export function normalizeSignalEngineThresholds(
  raw: unknown
): SignalEngineThresholds {
  const base = { ...DEFAULT_SIGNAL_ENGINE_THRESHOLDS };
  if (!raw || typeof raw !== "object") return base;
  const record = raw as Record<string, unknown>;
  const result = { ...base };
  (Object.keys(base) as (keyof SignalEngineThresholds)[]).forEach(key => {
    const n = Number(record[key]);
    if (Number.isFinite(n)) result[key] = n;
  });
  return result;
}

/** Building a label containing a literal double quote as a template literal
 * with this constant avoids the eslint `quotes: double` vs. prettier
 * disagreement on strings that contain double quotes (see CLAUDE.md). */
const DQ = String.fromCharCode(34);

const FIELD_GROUPS: {
  title: string;
  fields: { key: keyof SignalEngineThresholds; label: string; step?: number }[];
}[] = [
  {
    title: "Business Health (BH)",
    fields: [
      { key: "BH_STRONG", label: "Strong above" },
      { key: "BH_STABLE", label: "Stable at/above" },
      { key: "BH_CONCERN", label: "Concern at/above (else No Data)" },
    ],
  },
  {
    title: "External risk",
    fields: [
      { key: "RISK_HIGH", label: "High at/above" },
      { key: "RISK_MEDIUM", label: "Medium at/above (else Low)" },
    ],
  },
  {
    title: "Account Health (AH) signals",
    fields: [
      { key: "KPI_WARN", label: `${DQ}KPI Below Threshold${DQ} fires below` },
      {
        key: "AIRCOVER",
        label: `${DQ}RL Air Cover Needed${DQ} fires AH below`,
      },
    ],
  },
  {
    title: "Strategic Fit tiers",
    fields: [
      { key: "FIT_TIER_A", label: "Tier A at/above" },
      { key: "FIT_TIER_B", label: "Tier B at/above" },
      { key: "FIT_TIER_C", label: "Tier C at/above" },
      { key: "FIT_TIER_D", label: "Tier D at/above (else Tier E)" },
    ],
  },
  {
    title: "Opportunity",
    fields: [
      { key: "OPP_UPGRADE", label: "Upgrades Cautious → Open at/above" },
      { key: "OPP_MIN_EXPANSION", label: "Minimum to consider expansion" },
      { key: "OPP_FALLBACK_STRONG", label: "Fallback: Strong at/above" },
      { key: "OPP_FALLBACK_POSSIBLE", label: "Fallback: Possible at/above" },
    ],
  },
  {
    title: "Portfolio risk composite",
    fields: [
      { key: "PORTFOLIO_RISK_HIGH", label: "High at/above" },
      { key: "PORTFOLIO_RISK_LOW", label: "Low at/above (else None)" },
    ],
  },
  {
    title: "EWMA smoothing",
    fields: [
      { key: "EWMA_ALPHA", label: "Alpha (0–1)", step: 0.01 },
      { key: "EWMA_COLD_START_DAYS", label: "Cold-start window (days)" },
    ],
  },
  {
    title: "Hysteresis",
    fields: [
      {
        key: "UPGRADE_CONSISTENT_READINGS",
        label: "Consecutive readings to confirm an upgrade",
      },
      {
        key: "DOWNGRADE_CONSISTENT_READINGS",
        label: "Consecutive readings to confirm a downgrade",
      },
    ],
  },
];

export interface SignalEngineThresholdsFormProps {
  value: SignalEngineThresholds;
  onChange: (patch: Partial<SignalEngineThresholds>) => void;
}

const SignalEngineThresholdsForm: React.FC<SignalEngineThresholdsFormProps> = ({
  value,
  onChange,
}) => {
  return (
    <div className="space-y-5">
      {FIELD_GROUPS.map(group => (
        <div key={group.title}>
          <div className="mb-2 text-sm font-semibold text-gray-800">
            {group.title}
          </div>
          <div className="grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {group.fields.map(field => (
              <label
                key={field.key}
                className="flex items-center justify-between gap-3 text-xs text-gray-600"
              >
                <span>{field.label}</span>
                <input
                  type="number"
                  step={field.step ?? 1}
                  className="min-h-9 w-24 rounded-lg border border-[#dee2e6] bg-white px-2.5 py-1.5 text-right text-sm font-medium text-gray-900 outline-none focus:border-[#974ae4] focus:shadow-[0_0_0_1px_#974ae4]"
                  value={value[field.key]}
                  onChange={e =>
                    onChange({
                      [field.key]: Number(e.target.value),
                    } as Partial<SignalEngineThresholds>)
                  }
                />
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SignalEngineThresholdsForm;
